import { List, Map, Record } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { checkDate } from "common/helpers/Date";
import { IDocumentData } from "common/records/Document";
import { IImageData } from "common/records/Image";

export type EstimateTemplateKind = "standard" | "interior";

/**
 * A custom (non-product) line item stored inline on a template group. Carries its own
 * pricing/name — no price-list resolution needed (added unconditionally on apply).
 */
export interface IEstimateTemplateCustomProduct {
  uuid?: string;
  name: string;
  description?: string;
  price?: number;
  quantity?: number;
  uom?: string;
  image_ids?: number[];
  document_ids?: number[];
}

/**
 * The authored line-item order within a template group: an ordered list keyed by uuid. `type:"item"`
 * keys into `item_uuids`; `type:"custom"` matches a `custom_products` uuid. Honored on apply so
 * products and custom items interleave in the order the template author built them.
 */
export interface IEstimateTemplateLineItemOrder {
  type: "custom" | "item";
  order: string;
}

/**
 * A template group as stored in the `template` JSON. `item_uuids` maps each product UUID to its
 * option UUIDs — the stable, price-list-version-independent keys resolved at apply time.
 */
export interface IEstimateTemplateGroupContent {
  name: string;
  included: boolean;
  sort_order: number;
  item_uuids: { [productUuid: string]: string[] };
  custom_products?: IEstimateTemplateCustomProduct[];
  line_item_order?: IEstimateTemplateLineItemOrder[];
}

/** The heavy `template` payload — shipped by the index endpoint and hydrated onto the Record. */
export interface IEstimateTemplateContent {
  groups: IEstimateTemplateGroupContent[];
}

export interface IEstimateTemplateData {
  id: number;
  org_id: number;
  folder_id: Nullable<number>;
  name: string;
  kind: EstimateTemplateKind;
  sort_order: Nullable<number>;
  template: Nullable<IEstimateTemplateContent>;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  /** Files attached to the template's custom line items (Jbuilder side-loads these on show/index). */
  images?: IImageData[];
  documents?: IDocumentData[];
}

/**
 * Hard-typed Immutable shape of a stored custom product. `image_ids` / `document_ids` reference the
 * template's own attached files (hydrated on the record below); the save path uploads custom-line-item
 * files to the template and the apply path restores them onto the new line items.
 */
export interface IEstimateTemplateCustomProductRecord {
  uuid: Nullable<string>;
  name: Nullable<string>;
  description: Nullable<string>;
  price: Nullable<number>;
  quantity: Nullable<number>;
  uom: Nullable<string>;
  image_ids: List<number>;
  document_ids: List<number>;
}

export interface IEstimateTemplateLineItemOrderRecord {
  type: Nullable<"custom" | "item">;
  order: Nullable<string>;
}

export interface IEstimateTemplateGroupContentRecord {
  name: string;
  included: boolean;
  sort_order: number;
  /** product uuid → its option uuids; keys are data, so typed at the value level (not dropped). */
  item_uuids: Map<string, List<string>>;
  custom_products: List<EstimateTemplateCustomProductRecord>;
  line_item_order: List<EstimateTemplateLineItemOrderRecord>;
}

export interface IEstimateTemplateContentRecord {
  groups: List<EstimateTemplateGroupContentRecord>;
}

export interface IEstimateTemplateRecord {
  id: number;
  org_id: Nullable<number>;
  folder_id: Nullable<number>;
  name: Nullable<string>;
  kind: Nullable<EstimateTemplateKind>;
  sort_order: Nullable<number>;
  template: Nullable<EstimateTemplateContentRecord>;
  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
  loading: boolean;
  /** Ids of the template's attached files (the records live in the global image/document stores). */
  image_ids: List<number>;
  document_ids: List<number>;
}

const defaultCustomProductProps: IEstimateTemplateCustomProductRecord = {
  uuid: null,
  name: null,
  description: null,
  price: null,
  quantity: null,
  uom: null,
  image_ids: List(),
  document_ids: List(),
};

const defaultLineItemOrderProps: IEstimateTemplateLineItemOrderRecord = {
  type: null,
  order: null,
};

const defaultGroupContentProps: IEstimateTemplateGroupContentRecord = {
  name: "",
  included: false,
  sort_order: 0,
  item_uuids: Map(),
  custom_products: List(),
  line_item_order: List(),
};

const defaultContentProps: IEstimateTemplateContentRecord = {
  groups: List(),
};

export const defaultEstimateTemplateProps: IEstimateTemplateRecord = {
  id: 0,
  org_id: null,
  folder_id: null,
  name: null,
  kind: null,
  sort_order: null,
  template: null,
  created_at: null,
  updated_at: null,
  loading: false,
  image_ids: List(),
  document_ids: List(),
};

export class EstimateTemplateCustomProductRecord
  extends Record<IEstimateTemplateCustomProductRecord>(defaultCustomProductProps)
  implements IEstimateTemplateCustomProductRecord {}

export class EstimateTemplateLineItemOrderRecord
  extends Record<IEstimateTemplateLineItemOrderRecord>(defaultLineItemOrderProps)
  implements IEstimateTemplateLineItemOrderRecord {}

export class EstimateTemplateGroupContentRecord
  extends Record<IEstimateTemplateGroupContentRecord>(defaultGroupContentProps)
  implements IEstimateTemplateGroupContentRecord {}

export class EstimateTemplateContentRecord
  extends Record<IEstimateTemplateContentRecord>(defaultContentProps)
  implements IEstimateTemplateContentRecord {}

export class EstimateTemplateRecord
  extends Record<IEstimateTemplateRecord>(defaultEstimateTemplateProps)
  implements IEstimateTemplateRecord {}

const estimateTemplateCustomProductFromJSON = (
  custom: IEstimateTemplateCustomProduct,
): EstimateTemplateCustomProductRecord =>
  new EstimateTemplateCustomProductRecord({
    uuid: custom.uuid ?? null,
    name: custom.name ?? null,
    description: custom.description ?? null,
    price: custom.price ?? null,
    quantity: custom.quantity ?? null,
    uom: custom.uom ?? null,
    image_ids: List(custom.image_ids ?? []),
    document_ids: List(custom.document_ids ?? []),
  });

const estimateTemplateLineItemOrderFromJSON = (
  entry: IEstimateTemplateLineItemOrder,
): EstimateTemplateLineItemOrderRecord =>
  new EstimateTemplateLineItemOrderRecord({
    type: entry.type ?? null,
    order: entry.order ?? null,
  });

const estimateTemplateGroupContentFromJSON = (
  group: IEstimateTemplateGroupContent,
): EstimateTemplateGroupContentRecord =>
  new EstimateTemplateGroupContentRecord({
    name: group.name ?? "",
    included: group.included ?? false,
    sort_order: group.sort_order ?? 0,
    item_uuids: Map<string, List<string>>(
      Object.entries(group.item_uuids ?? {}).map(([productUuid, optionUuids]) => [
        productUuid,
        List(optionUuids ?? []),
      ]),
    ),
    custom_products: List((group.custom_products ?? []).map(estimateTemplateCustomProductFromJSON)),
    line_item_order: List((group.line_item_order ?? []).map(estimateTemplateLineItemOrderFromJSON)),
  });

/**
 * Hydrates the heavy `template` payload into hard-typed Immutable Records (or null when absent),
 * picking ONLY the fields occ-app models. This is a strict allowlist — any unmodeled top-level,
 * group, or custom-product field (legacy `etg.items`, interior room data, per-group tax/markup
 * config, or anything else lurking in older rows) is dropped on hydration rather than parked in the
 * store. Stale templates are forced onto the current standard as they're read. `item_uuids` stays a
 * value-typed `Map<uuid, List<optionUuid>>` — its keys are product-uuid data, not schema.
 */
export const estimateTemplateContentFromJSON = (
  template: Nullable<IEstimateTemplateContent>,
): Nullable<EstimateTemplateContentRecord> =>
  template
    ? new EstimateTemplateContentRecord({
        groups: List((template.groups ?? []).map(estimateTemplateGroupContentFromJSON)),
      })
    : null;

export const estimateTemplateFromJSON = (json: Partial<IEstimateTemplateData>): EstimateTemplateRecord =>
  new EstimateTemplateRecord({
    id: json.id ?? defaultEstimateTemplateProps.id,
    org_id: json.org_id ?? null,
    folder_id: json.folder_id ?? null,
    name: json.name ?? null,
    kind: json.kind ?? null,
    sort_order: json.sort_order ?? null,
    template: estimateTemplateContentFromJSON(json.template ?? null),
    created_at: json.created_at ? checkDate(json.created_at) : null,
    updated_at: json.updated_at ? checkDate(json.updated_at) : null,
    loading: false,
    // The image/document records themselves are side-loaded into the global stores by the reducer;
    // the template keeps only their ids so the apply path can restore files onto custom line items.
    image_ids: List((json.images ?? []).map((image) => image.id)),
    document_ids: List((json.documents ?? []).map((document) => document.id)),
  });
